import ModeToggle from '@/Components/mode-toggle';
import { Badge } from '@/Components/ui/badge';
import { Button } from '@/Components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/Components/ui/dialog';
import {
    NavigationMenu,
    NavigationMenuItem,
    NavigationMenuList,
} from '@/Components/ui/navigation-menu';
import { Sheet, SheetTrigger } from '@/Components/ui/sheet';
import { Link, router } from '@inertiajs/react';
import * as Icons from 'lucide-react';
import { LucideIcon } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import MobileMenu from './MobileMenu';
import SearchDialog from './SearchDialog';

interface Solution {
    title: string;
    description: string;
    icon: keyof typeof Icons;
    href: string;
    badge?: string;
}

interface QuickAction {
    title: string;
    description: string;
    icon: LucideIcon;
    onClick: () => void;
    badge?: string;
    color?: string;
}

interface NavbarProps {
    navLinks: Array<{
        href: string;
        label: string;
        active?: boolean;
    }>;
    solutions: Solution[];
    companyInfo: {
        name: string;
        logo: string;
    };
    contactInfo: {
        whatsapp: string | null;
    };
}

const Navbar: React.FC<NavbarProps> = ({
    navLinks,
    solutions,
    companyInfo,
    contactInfo,
}) => {
    const [scrolled, setScrolled] = useState(false);
    const [isVisible, setIsVisible] = useState(true);
    const [lastScrollY, setLastScrollY] = useState(0);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [isQuickActionsOpen, setIsQuickActionsOpen] = useState(false);
    const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            setIsVisible(lastScrollY > currentScrollY || currentScrollY < 50);
            setScrolled(currentScrollY > 50);
            setLastScrollY(currentScrollY);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [lastScrollY]);

    const renderIcon = (iconName: keyof typeof Icons): JSX.Element | null => {
        const IconComponent = Icons[iconName] as React.ElementType;
        return IconComponent ? (
            <IconComponent className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
        ) : null;
    };

    const quickActions: QuickAction[] = [
        {
            title: 'Farmer Support',
            description: 'Connect with our agricultural experts via WhatsApp',
            icon: Icons.MessageSquare,
            color: 'text-emerald-500',
            badge: contactInfo.whatsapp ? 'Online' : 'Offline',
            onClick: () => {
                if (contactInfo.whatsapp) {
                    window.open(
                        `https://wa.me/${contactInfo.whatsapp.replace(/\D/g, '')}`,
                        '_blank',
                    );
                }
            },
        },
        {
            title: 'Climate-Smart Practices',
            description: 'Access our knowledge base for eco-friendly farming',
            icon: Icons.Leaf,
            color: 'text-emerald-600',
            badge: 'Coming Soon',
            onClick: () => router.visit(route('climate-smart-practices')),
        },
        {
            title: 'Community Forum',
            description: 'Connect and share knowledge with fellow farmers',
            icon: Icons.Users,
            color: 'text-emerald-500',
            badge: 'WhatsApp',
            onClick: () => {
                window.open(
                    'https://whatsapp.com/channel/0029VacWOOCEAKWEDmtxuq17',
                    '_blank',
                );
            },
        },
        {
            title: 'Market Access',
            description: 'Connect with buyers and access better markets',
            icon: Icons.Store,
            color: 'text-amber-500',
            badge: 'Insights',
            onClick: () => router.visit('/market'),
        },
    ];

    return (
        <header
            className={`fixed left-0 right-0 top-0 z-50 transition-all duration-500 ${
                isVisible ? 'translate-y-0' : '-translate-y-full'
            }`}
        >
            <div
                className={`mx-4 my-3 rounded-xl transition-all duration-300 ${
                    scrolled
                        ? 'bg-white/90 shadow-lg backdrop-blur-md dark:bg-gray-800/90'
                        : 'border border-emerald-100 bg-white/80 dark:border-emerald-800/30 dark:bg-gray-800/80'
                }`}
            >
                <div className="container mx-auto flex items-center justify-between p-3">
                    {/* Logo Section */}
                    <Link
                        href="/"
                        className="group flex items-center gap-3 transition-all duration-300 hover:scale-105"
                    >
                        <img
                            src={companyInfo.logo}
                            alt={`${companyInfo.name} Logo`}
                            className="h-8 w-auto"
                        />
                        <span className="bg-gradient-to-r from-emerald-600 to-amber-600 bg-clip-text text-2xl font-bold text-transparent transition-all duration-300 group-hover:from-emerald-500 group-hover:to-amber-500">
                            {companyInfo.name}
                        </span>
                    </Link>

                    {/* Desktop Navigation */}
                    <nav className="hidden md:block">
                        <NavigationMenu>
                            <NavigationMenuList className="gap-2">
                                {navLinks.map((link) => (
                                    <NavigationMenuItem key={link.href}>
                                        <Link
                                            href={link.href}
                                            className={`group inline-flex h-9 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors hover:bg-gradient-to-r hover:from-emerald-50 hover:to-amber-50 hover:text-emerald-900 focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 dark:hover:from-emerald-900/30 dark:hover:to-amber-900/30 dark:hover:text-emerald-100 ${
                                                link.active
                                                    ? 'bg-gradient-to-r from-emerald-100 to-amber-100 text-emerald-900 dark:from-emerald-900/50 dark:to-amber-900/50 dark:text-emerald-100'
                                                    : 'text-emerald-700 dark:text-emerald-300'
                                            }`}
                                        >
                                            {link.label}
                                        </Link>
                                    </NavigationMenuItem>
                                ))}
                            </NavigationMenuList>
                        </NavigationMenu>
                    </nav>

                    {/* Right Side Actions */}
                    <div className="flex items-center gap-2">
                        {/* Solutions Dialog */}
                        <div className="hidden md:block">
                            <Dialog
                                open={isSolutionsOpen}
                                onOpenChange={setIsSolutionsOpen}
                            >
                                <DialogTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className="bg-gradient-to-r from-emerald-50 to-amber-50 hover:from-emerald-100 hover:to-amber-100 dark:from-emerald-900/30 dark:to-amber-900/30 dark:hover:from-emerald-800/50 dark:hover:to-amber-800/50"
                                    >
                                        <Icons.Lightbulb className="mr-2 h-4 w-4" />
                                        Solutions
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="sm:max-w-[900px]">
                                    <DialogHeader>
                                        <DialogTitle className="text-2xl font-bold text-emerald-900 dark:text-emerald-100">
                                            Agritech Solutions
                                        </DialogTitle>
                                        <DialogDescription className="text-emerald-700 dark:text-emerald-300">
                                            Discover our comprehensive range of
                                            agricultural solutions designed to
                                            enhance your farming experience
                                        </DialogDescription>
                                    </DialogHeader>
                                    <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-2 lg:grid-cols-3">
                                        {solutions.map((solution) => (
                                            <Link
                                                key={solution.title}
                                                href={solution.href}
                                                onClick={() =>
                                                    setIsSolutionsOpen(false)
                                                }
                                                className="group relative overflow-hidden rounded-xl border border-emerald-100 bg-white/50 p-6 transition-all hover:border-emerald-200 hover:bg-gradient-to-br hover:from-emerald-50 hover:to-amber-50 hover:shadow-lg dark:border-emerald-800/30 dark:bg-gray-800/50 dark:hover:border-emerald-700/30 dark:hover:from-emerald-900/30 dark:hover:to-amber-900/30"
                                            >
                                                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-emerald-100 transition-colors group-hover:bg-emerald-200 dark:bg-emerald-900/50 dark:group-hover:bg-emerald-800/70">
                                                    {renderIcon(solution.icon)}
                                                </div>
                                                <div className="space-y-2">
                                                    <div className="flex items-center gap-2">
                                                        <h3 className="font-semibold text-emerald-900 dark:text-emerald-100">
                                                            {solution.title}
                                                        </h3>
                                                        {solution.badge && (
                                                            <Badge
                                                                variant="secondary"
                                                                className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100"
                                                            >
                                                                {solution.badge}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-emerald-700 dark:text-emerald-300">
                                                        {solution.description}
                                                    </p>
                                                </div>
                                                <div className="absolute bottom-4 right-4 opacity-0 transition-opacity group-hover:opacity-100">
                                                    <Icons.ArrowUpRight className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </DialogContent>
                            </Dialog>
                        </div>

                        {/* Quick Actions Dialog */}
                        <Dialog
                            open={isQuickActionsOpen}
                            onOpenChange={setIsQuickActionsOpen}
                        >
                            <DialogTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="relative hidden items-center justify-center hover:bg-gradient-to-r hover:from-emerald-50 hover:to-amber-50 dark:hover:from-emerald-900/30 dark:hover:to-amber-900/30 md:flex"
                                >
                                    <Icons.Rocket className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                                    <Badge
                                        variant="secondary"
                                        className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-amber-100 p-0 text-[10px] text-amber-800 dark:bg-amber-900 dark:text-amber-100"
                                    >
                                        {quickActions.length}
                                    </Badge>
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-md">
                                <DialogHeader>
                                    <DialogTitle className="text-emerald-900 dark:text-emerald-100">
                                        Quick Actions
                                    </DialogTitle>
                                    <DialogDescription className="text-emerald-700 dark:text-emerald-300">
                                        Access frequently used features and
                                        support options
                                    </DialogDescription>
                                </DialogHeader>
                                <div className="mt-4 grid gap-4">
                                    {quickActions.map((action) => (
                                        <button
                                            key={action.title}
                                            onClick={() => {
                                                setIsQuickActionsOpen(false);
                                                action.onClick();
                                            }}
                                            className="group flex w-full items-center gap-4 rounded-lg border border-emerald-100 bg-white/50 p-4 text-left transition-all hover:border-emerald-200 hover:bg-gradient-to-r hover:from-emerald-50 hover:to-amber-50 hover:shadow-md dark:border-emerald-800/30 dark:bg-gray-800/50 dark:hover:border-emerald-700/30 dark:hover:from-emerald-900/30 dark:hover:to-amber-900/30"
                                        >
                                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-emerald-100 transition-colors group-hover:bg-emerald-200 dark:bg-emerald-900/50 dark:group-hover:bg-emerald-800/70">
                                                <action.icon
                                                    className={`h-6 w-6 ${action.color}`}
                                                />
                                            </div>
                                            <div className="flex-1 space-y-1">
                                                <div className="flex items-center justify-between">
                                                    <p className="font-medium text-emerald-900 dark:text-emerald-100">
                                                        {action.title}
                                                    </p>
                                                    {action.badge && (
                                                        <Badge
                                                            variant="secondary"
                                                            className="flex items-center justify-center bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100"
                                                        >
                                                            {action.badge}
                                                        </Badge>
                                                    )}
                                                </div>
                                                <p className="text-sm text-emerald-700 dark:text-emerald-300">
                                                    {action.description}
                                                </p>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </DialogContent>
                        </Dialog>

                        {/* Search Dialog Trigger */}
                        <Dialog
                            open={isSearchOpen}
                            onOpenChange={setIsSearchOpen}
                        >
                            <DialogTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="hover:bg-gradient-to-r hover:from-emerald-50 hover:to-amber-50 dark:hover:from-emerald-900/30 dark:hover:to-amber-900/30"
                                >
                                    <Icons.Search className="h-5 w-5 text-emerald-700 dark:text-emerald-300" />
                                </Button>
                            </DialogTrigger>

                            {/* Search Dialog Component */}
                            <SearchDialog
                                onOpenChange={setIsSearchOpen}
                                suggestions={
                                    (window as any)?.__INITIAL_STATE__
                                        ?.components?.Search?.suggestions
                                }
                            />
                        </Dialog>

                        {/* Theme Toggle */}
                        <ModeToggle />

                        {/* Mobile Menu */}
                        <Sheet>
                            <SheetTrigger asChild>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="relative md:hidden"
                                >
                                    <Icons.Menu className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                                </Button>
                            </SheetTrigger>
                            <MobileMenu
                                solutions={solutions}
                                quickActions={quickActions}
                                navLinks={navLinks}
                            />
                        </Sheet>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Navbar;
